import type { ComponentType, ReactNode } from "react";
import { CheckCircle2, Clock } from "lucide-react";

// Shared status card for the portal item surfaces. The recipient-facing
// "in progress" and "under review" lines used to render as bare <p> text;
// wrapping them in a tinted, bordered alert card makes the state read at a
// glance and keeps Form / Upload / Education consistent. Brand tokens only.

type SubmissionStatusTone = "progress" | "review";

const TONE_CARD: Record<SubmissionStatusTone, string> = {
  // In progress (saved / submitting) — calm info tint.
  progress: "bg-status-info/10 border-status-info",
  // Under review (handed off to staff) — gold tint, matching the "Under
  // review" status pill's progress tone.
  review: "bg-gold/10 border-gold/40",
};

const TONE_ICON: Record<
  SubmissionStatusTone,
  ComponentType<{ size?: number; strokeWidth?: number; "aria-hidden"?: boolean; className?: string }>
> = {
  progress: Clock,
  review: CheckCircle2,
};

const TONE_ICON_COLOR: Record<SubmissionStatusTone, string> = {
  progress: "text-status-info",
  review: "text-gold-shadow",
};

export function SubmissionStatusCard({
  tone,
  title,
  children,
  "data-testid": testId,
}: {
  tone: SubmissionStatusTone;
  title: string;
  /** Optional supporting line beneath the title. */
  children?: ReactNode;
  "data-testid"?: string;
}) {
  const Icon = TONE_ICON[tone];
  return (
    <div
      className={`flex items-start gap-space-3 p-space-3 border rounded-md ${TONE_CARD[tone]}`}
      role="status"
      aria-live="polite"
      data-testid={testId}
    >
      <Icon size={18} strokeWidth={1.5} aria-hidden className={`shrink-0 mt-[2px] ${TONE_ICON_COLOR[tone]}`} />
      <div className="flex flex-col gap-space-1">
        <span className="text-small font-medium text-onyx">{title}</span>
        {children && <span className="text-small text-gray-mid">{children}</span>}
      </div>
    </div>
  );
}
